import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface PageTransitionProps {
  children: React.ReactNode;
  isLoading?: boolean;
}

const PageTransition: React.FC<PageTransitionProps> = ({ children, isLoading = false }) => {
  useEffect(() => {
    // Handle any side effects when loading changes if needed
  }, [isLoading]);

  return (
    <AnimatePresence mode="wait">
      {isLoading ? (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
        >
          {/* Background grid */}
          <div className="fixed inset-0 opacity-10">
            <div 
              className="absolute inset-0 bg-cyber-grid" 
              style={{ backgroundSize: '60px 60px' }}
            />
          </div>

          {/* Loading animation */}
          <div className="relative z-10 text-center">
            {/* BR Logo */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="mb-8"
            >
              <img 
                src="/br-logo.png" 
                alt="BR Logo" 
                className="w-16 h-16 mx-auto opacity-80 cyber-glow" 
              />
            </motion.div>

            {/* Loading text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-cyan-400 font-mono text-lg mb-6 cyber-glow"
            >
              <motion.span
                animate={{ opacity: [1, 0.5, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                Initializing neural pathways...
              </motion.span>
            </motion.div>

            {/* Progress bars */}
            <div className="w-80 mx-auto space-y-3">
              {[
                { label: 'NEURAL_CORE', delay: 0.1, color: 'bg-cyan-400' },
                { label: 'MEMORY_BANKS', delay: 0.3, color: 'bg-green-400' },
                { label: 'INTERFACE_LAYER', delay: 0.5, color: 'bg-purple-400' }
              ].map((item, i) => (
                <div key={i} className="text-left">
                  <div className="text-gray-400 text-xs font-mono mb-1">
                    {item.label}
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-1">
                    <motion.div
                      className={`h-1 rounded-full ${item.color}`}
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ 
                        delay: item.delay, 
                        duration: 0.8,
                        ease: "easeOut"
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Status indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-8 flex items-center justify-center space-x-2 text-green-400 text-sm font-mono"
            >
              <motion.div
                className="w-2 h-2 bg-green-400 rounded-full"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
              <span>SYSTEMS ONLINE</span>
            </motion.div>
          </div>

          {/* Scan line effect */}
          <motion.div
            className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-60"
            animate={{ y: [0, '100vh'] }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              ease: "linear" 
            }}
          />
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageTransition;